import { useMemo } from "react";
import { useStore } from "zustand";

import type {
  CameraCatalogEntry,
  LinkQualityPayload,
  RobotConfigPayload,
  RobotConfigStatus,
  RobotLimits,
  RobotStatusPayload,
} from "../modules/teleop-state";
import { useRobotState } from "./useRobotState";

export interface UseRobotResult {
  status: RobotConfigStatus;
  config: RobotConfigPayload | null;
  limits: RobotLimits | null;
  ready: boolean;
  /** Camera catalog from robot-config (stable reference while the config is unchanged). */
  cameras: CameraCatalogEntry[];
  telemetry: RobotStatusPayload | null;
  /** Uplink link-quality reported by the robot (DataChannel `event: link-quality`). */
  linkQuality: LinkQualityPayload | null;
}

/**
 * Consolidated read hook for robot business state: config + telemetry + camera
 * catalog in one subscription, so pages don't wire a config hook, a telemetry
 * `useEffect`, and a cameras `useMemo` separately. Each slice is itself a zustand
 * store — `useStore` subscribes directly, no `on()`/`useEffect`/`useState` needed.
 */
export function useRobot(): UseRobotResult {
  const robotState = useRobotState();

  const { status, config } = useStore(robotState.robotConfig);
  const telemetry = useStore(robotState.robotTelemetry);
  const linkQuality = useStore(robotState.linkQuality);

  // Memoized on config identity so callers (e.g. useCameraBootstrap's cameras effect)
  // don't see a new array reference on every render while config is unchanged.
  const cameras = useMemo(() => config?.cameras ?? [], [config]);

  return {
    status,
    config,
    limits: config?.limits ?? null,
    ready: status === "ready",
    cameras,
    telemetry,
    linkQuality,
  };
}
